import { useState } from "react";
import { CopyLinkButton, careersUrl } from "@/components/app/CopyLinkButton";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, Plus, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { EmptyState, PageHeader, SkeletonRows } from "@/components/app/Common";
import { JobStatusBadge, StatusChip, analysisLabel } from "@/components/app/StatusChip";
import { cn } from "@/lib/utils";
import { jobMeta } from "@/pages/job/LegalFields";
import { deleteItem, deleteJob, plural, TrashButton } from "@/lib/deleteItem";

const FILTERS = [["all", "All"], ["draft", "Draft"], ["open", "Open"], ["paused", "Paused"], ["closed", "Closed"]];

export const jobLink = (j) => (j.status === "draft" ? `/app/jobs/new?draft=${j.id}` : `/app/jobs/${j.id}/workspace`);

export default function JobsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(new Set());
  const canCreate = STAFF.includes(user.role);
  const { data: jobs, isLoading } = useQuery({ queryKey: ["jobs"], queryFn: () => get("/jobs") });
  const count = (k) => (jobs || []).filter((j) => k === "all" || j.status === k).length;
  const shown = (jobs || []).filter((j) => filter === "all" || j.status === filter);
  const drop = (ids) => {
    qc.setQueryData(["jobs"], (old) => (old || []).filter((j) => !ids.includes(j.id)));
    setSel((s) => new Set([...s].filter((x) => !ids.includes(x))));
    qc.invalidateQueries({ queryKey: ["jobs"] }); qc.invalidateQueries({ queryKey: ["candidates"] });
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = shown.length > 0 && shown.every((j) => sel.has(j.id));
  const bulk = () => {
    const ids = [...sel];
    const apps = (jobs || []).filter((j) => sel.has(j.id)).reduce((t, j) => t + (j.candidate_count || 0), 0);
    deleteItem({ title: `Delete ${plural(ids.length, "job")}?`, typeToConfirm: "DELETE", method: "post", url: "/jobs/bulk-delete", data: { ids },
      body: `This also removes ${plural(apps, "application")} with their invites, interviews, notes, scores and files. Candidates who only applied to these jobs are deleted; candidates in other jobs are kept.`,
      success: `Deleted ${plural(ids.length, "job")}`, onDone: () => drop(ids) });
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <PageHeader
        title="Jobs"
        subtitle="Every role you're hiring for, from draft to closed."
        actions={canCreate && <Button data-tour="create-job" data-testid="jobs-create-btn" onClick={() => nav("/app/jobs/new")}><Plus size={16} /> Create job</Button>}
      />
      <div className="flex flex-wrap items-center gap-2" data-tour="pt-jobs-filters" data-testid="jobs-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`jobs-filter-${k}`} onClick={() => setFilter(k)}
            className={cn("rounded-full border px-3 py-1 text-sm transition-colors duration-150",
              filter === k ? "border-brand bg-brand-tint text-brand" : "border-line bg-white text-ink-secondary hover:bg-subtle")}>
            {label} <span className="ml-1 text-xs text-ink-muted">{count(k)}</span>
          </button>
        ))}
        {canCreate && sel.size > 0 && <Button variant="outline" size="sm" className="ml-auto text-risk hover:text-risk" onClick={bulk} data-testid="jobs-bulk-delete"><Trash2 size={14} /> Delete selected ({sel.size})</Button>}
      </div>
      {isLoading ? <SkeletonRows rows={5} /> : !shown.length ? (
        <EmptyState
          testId="jobs-empty"
          icon={Briefcase}
          title={jobs?.length ? "No jobs with this status" : "No jobs yet"}
          text={jobs?.length ? "Try a different filter." : "Create your first job by pasting a job description. The AI drafts the requirements for you to review."}
          action={canCreate && !jobs?.length && <Button variant="outline" onClick={() => nav("/app/jobs/new")}>Create a job</Button>}
        />
      ) : (
        <div className="panel overflow-hidden max-lg:overflow-x-auto">
          <table className="w-full text-sm" data-tour="pt-jobs-table" data-testid="jobs-table">
            <thead className="bg-subtle text-left text-xs text-ink-muted">
              <tr>
                {canCreate && <th className="w-10 pl-4"><Checkbox checked={allOn} onCheckedChange={(v) => setSel(v ? new Set(shown.map((j) => j.id)) : new Set())} aria-label="Select all jobs" data-testid="jobs-select-all" /></th>}
                <th className="px-6 py-3 font-medium">Job</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Setup</th>
                <th className="px-4 py-3 font-medium text-right">Candidates</th>
                <th className="px-6 py-3 font-medium text-right">Updated</th>
                <th className="px-4 py-3 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {shown.map((j) => (
                <tr key={j.id} data-testid={`job-row-${j.id}`} onClick={() => nav(jobLink(j))} onKeyDown={(e) => e.key === "Enter" && nav(jobLink(j))} tabIndex={0} className="tl-row">
                  {canCreate && <td className="w-10 pl-4" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.has(j.id)} onCheckedChange={() => toggle(j.id)} aria-label={`Select ${j.title || "job"}`} data-testid={`job-select-${j.id}`} /></td>}
                  <td className="px-6 py-3">
                    <p className="font-medium text-ink">{j.title || "Untitled job"}</p>
                    <p className="text-xs text-ink-muted" data-testid={`job-meta-${j.id}`}>{jobMeta(j).join(" · ") || "—"}</p>
                  </td>
                  <td className="px-4 py-3"><JobStatusBadge status={j.status} /></td>
                  <td className="px-4 py-3"><StatusChip status={analysisLabel(j.analysis_status)} /></td>
                  <td className="px-4 py-3 text-right">{j.candidate_count}</td>
                  <td className="px-6 py-3 text-right text-xs text-ink-muted">{formatDistanceToNow(new Date(j.updated_at), { addSuffix: true })}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {j.status === "open" && <CopyLinkButton url={careersUrl(j)} iconOnly size="sm" variant="ghost" testId={`jobs-list-copy-careers-link-${j.id}`} />}
                    {canCreate && <TrashButton label={`Delete ${j.title || "job"}`} testId={`delete-job-${j.id}`} onClick={() => deleteJob(j, () => drop([j.id]))} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
